import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import {
  SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import darkSelectStyles from '../../../shared/darkSelectStyles';
import NoteTemplateForm from './NoteTemplateForm';

const chipStyle = {
  fontSize: '12px',
  fontWeight: 500,
  padding: '3px 8px 3px 10px',
  borderRadius: 'var(--radius-full)',
  background: 'var(--sl-bg-primary)',
  border: '1px solid var(--sl-border-primary)',
  color: 'var(--sl-text-secondary)',
};

const dotStyle = {
  width: '8px',
  height: '8px',
  borderRadius: 'var(--radius-full)',
  background: 'var(--sl-bg-brand-solid)',
  marginRight: '6px',
  flexShrink: 0,
};

const NoteChip = ({ note, onRemove }) => {
  const {
    attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition,
  } = useSortable({ id: note.id });

  return (
    <span
      ref={setNodeRef}
      className="mr-2 mb-2 d-inline-flex align-items-center"
      style={{ ...chipStyle, transform: CSS.Translate.toString(transform), transition }}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        data-testid={`reorder-note-${note.id}`}
        className="btn btn-link p-0 mr-2 d-inline-flex"
        style={{
          color: 'var(--sl-text-quaternary)', lineHeight: 1, cursor: 'grab', touchAction: 'none',
        }}
        aria-label={`Reorder ${note.title}`}
        {...attributes}
        {...listeners}
      >
        <i className="fas fa-grip-vertical" />
      </button>
      <span style={dotStyle} />
      {note.title}
      <button
        type="button"
        data-testid={`remove-note-${note.id}`}
        className="btn btn-link p-0 ml-2 d-inline-flex"
        style={{ color: 'var(--sl-text-quaternary)', lineHeight: 1 }}
        onClick={() => onRemove(note.id)}
        aria-label={`Remove ${note.title}`}
      >
        <i className="fal fa-times" />
      </button>
    </span>
  );
};

NoteChip.propTypes = {
  note: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const NoteChips = ({
  notes, noteTemplates, onChange,
  csrfToken, teamId, noteTemplatesChanged, eventTemplateForForm, eventTemplateForFormChanged,
}) => {
  const attachedIds = notes.map((n) => n.id);
  const available = noteTemplates
    .filter((t) => !attachedIds.includes(t.id))
    .sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()))
    .map((t) => ({ value: t.id, label: t.title }));

  const removeNote = (id) => onChange(notes.filter((n) => n.id !== id));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const reorderNotes = ({ active, over }) => {
    if (!over) return;
    if (active.id === over.id) return;
    onChange(arrayMove(notes, attachedIds.indexOf(active.id), attachedIds.indexOf(over.id)));
  };

  const addNote = (id) => {
    const template = noteTemplates.find((t) => t.id === id);
    if (!template) return;
    onChange([...notes, { id: template.id, title: template.title, body: template.body }]);
  };

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={reorderNotes}>
          <SortableContext items={attachedIds} strategy={rectSortingStrategy}>
            {notes.map((note) => (
              <NoteChip key={note.id} note={note} onRemove={removeNote} />
            ))}
          </SortableContext>
        </DndContext>

        <div className="mb-2" style={{ minWidth: '240px' }}>
          <Select
            classNamePrefix="note-add"
            aria-label="Add a note template"
            styles={darkSelectStyles}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            placeholder="Add a note template…"
            value={null}
            controlShouldRenderValue={false}
            options={available}
            onChange={(option) => { if (option) addNote(option.value); }}
          />
        </div>
      </div>

      <div className="mt-2">
        <NoteTemplateForm
          csrfToken={csrfToken}
          teamId={teamId}
          noteTemplates={noteTemplates}
          noteTemplatesChanged={noteTemplatesChanged}
          eventTemplateForForm={eventTemplateForForm}
          eventTemplateForFormChanged={eventTemplateForFormChanged}
        />
      </div>
    </div>
  );
};

NoteChips.propTypes = {
  notes: PropTypes.array.isRequired,
  noteTemplates: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  csrfToken: PropTypes.string.isRequired,
  teamId: PropTypes.number.isRequired,
  noteTemplatesChanged: PropTypes.func.isRequired,
  eventTemplateForForm: PropTypes.object.isRequired,
  eventTemplateForFormChanged: PropTypes.func.isRequired,
};

export default NoteChips;
